import { CaretRightOutlined } from '@ant-design/icons';
import { Collapse, Flex, Steps, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import styled, { keyframes } from 'styled-components';
import { Blue1, Blue5, BluePrimary, Gray7, Gray8 } from '../../../../styles';
import { chatItemType } from '../../../../types';
import { GenericTool, TOOL_RENDERERS } from '../ToolRenderers/index';

const { Text } = Typography;

// Keyframe animations
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const fadeSlide = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  10% {
    opacity: 1;
    transform: translateY(0);
  }
  90% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-30px);
  }
`;

// Styled components
const ThinkingContainer = styled.div`
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
  margin: 8px 0;
`;

const ThinkingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${(props) => (props.$hasCarousel ? '12px' : '0')};
`;

const SpinnerIcon = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${Blue1};
  border-top-color: ${BluePrimary};
  border-radius: 50%;
  animation: ${rotate} 0.8s linear infinite;
`;

const ThinkingTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${Gray8};
`;

const ExpandText = styled(Text)`
  font-size: 14px;
  color: ${Gray8};
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;

  &:hover {
    color: ${Blue5};
    text-decoration: underline;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  min-height: 24px;
  margin-left: 36px;
`;

const CarouselText = styled(Text)`
  position: absolute;
  font-size: 14px;
  color: ${Gray7};
  font-style: italic;
  animation: ${fadeSlide} 4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
`;

const StyledSteps = styled(Steps)`
  margin-top: 16px;

  .ant-steps-item-title {
    font-size: 14px;
    font-weight: 500;
    color: ${Gray8};
  }

  .ant-steps-item-description {
    margin-top: 8px;
  }
`;

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-item {
    border-bottom: none;
  }

  .ant-collapse-header {
    font-weight: 400;
    color: ${Gray7};
    padding: 0 !important;
    font-size: 13px;
  }

  .ant-collapse-content {
    border-top: none;
  }

  .ant-collapse-content-box {
    padding: 8px 0 0 0;
    background: transparent;
  }
`;

const TextContent = styled.div`
  color: ${Gray8};

  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

/**
 * Extracts meaningful text from an item for the carousel
 */
function getCarouselText(item) {
  if (!item) return 'Processing...';

  if (item.type === 'text') {
    // Truncate long text for carousel
    const text = item.text || '';
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  }

  if (item.type === 'tool') {
    const toolName = item.name || 'Unknown tool';
    // Try to get a meaningful description from args
    if (item.args) {
      const firstArg = Object.values(item.args)[0];
      if (typeof firstArg === 'string' && firstArg.length < 60) {
        return `Calling ${toolName}: ${firstArg}`;
      }
    }
    return `Calling ${toolName}...`;
  }

  return 'Processing...';
}

/**
 * Generates a step title and renders the appropriate component
 */
function getStepInfo(item, index) {
  if (item.type === 'text') {
    const text = item.text || '';
    const title = text.length > 60 ? `${text.substring(0, 60)}...` : text;
    return {
      title: title || 'Working...',
      status: 'finish',
      description: (
        <StyledCollapse
          ghost
          size="small"
          expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
          items={[
            {
              key: '1',
              label: 'View details',
              children: (
                <TextContent>
                  <Markdown>{text}</Markdown>
                </TextContent>
              ),
            },
          ]}
        />
      ),
    };
  }

  if (item.type === 'tool') {
    const hasResponse = item.response !== null && item.response !== undefined;
    const toolDisplayName = item.name
      ? item.name
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : 'Tool';

    // Use the appropriate tool renderer
    const ToolRenderer = TOOL_RENDERERS[item.name] || GenericTool;

    return {
      title: toolDisplayName,
      status: hasResponse ? 'finish' : 'process',
      description: <ToolRenderer item={item} />,
    };
  }

  return {
    title: `Step ${index + 1}`,
    status: 'finish',
    description: null,
  };
}

/**
 * ThinkingBlock component - Shows a collapsible thinking indicator
 * with a text carousel and detailed steps view
 */
function ThinkingBlock({ items, isThinking = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [carouselTexts, setCarouselTexts] = useState([]);
  const [currentText, setCurrentText] = useState('');
  const prevItemsLengthRef = useRef(0);

  // Extract carousel texts from items and track new additions
  useEffect(() => {
    const texts = items.map(getCarouselText).filter(Boolean);
    setCarouselTexts(texts);

    // If a new item was added, show its text
    if (texts.length > prevItemsLengthRef.current && texts.length > 0) {
      setCurrentText(texts[texts.length - 1]);
    }

    prevItemsLengthRef.current = texts.length;
  }, [items]);

  // Create steps from items
  const stepsItems = items.map((item, index) => {
    const stepInfo = getStepInfo(item, index);
    return {
      title: stepInfo.title,
      status: stepInfo.status,
      description: stepInfo.description,
    };
  });

  return (
    <ThinkingContainer>
      <Flex justify="space-between" align="flex-start">
        <div style={{ flex: 1 }}>
          <ThinkingHeader $hasCarousel={isThinking && carouselTexts.length > 0}>
            {isThinking && <SpinnerIcon />}
            <ThinkingTitle>{isThinking ? 'Working...' : 'Work Completed'}</ThinkingTitle>
          </ThinkingHeader>

          {isThinking && carouselTexts.length > 0 && (
            <CarouselContainer>
              <CarouselText key={currentText}>{currentText}</CarouselText>
            </CarouselContainer>
          )}
        </div>

        <ExpandText onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Hide my work' : 'Click to see my work'}
        </ExpandText>
      </Flex>

      {isExpanded && (
        <StyledSteps
          progressDot
          direction="vertical"
          current={stepsItems.length}
          items={stepsItems}
        />
      )}
    </ThinkingContainer>
  );
}

ThinkingBlock.propTypes = {
  items: PropTypes.arrayOf(chatItemType).isRequired,
  isThinking: PropTypes.bool,
};

export default ThinkingBlock;
